'use client'

import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useIsMounted } from 'usehooks-ts'

import {
  trackTypeLocalSearch,
  trackViewDataIndukKecamatan,
} from '@/utils/tracker/modules/datainduk'
import { TrackerSection, TrackerSubSection } from '@/utils/tracker/shared'
import { BasicDataRow } from '@/apis/types'
import {
  useFetchSearchYayasan,
  useWilayahBreadcrumbs,
} from '@/apis/useDataIndukYayasan'

import { Breadcrumbs } from '@/components/Breadcrumbs'
import { Container } from '@/components/Container'
import { DownloadSurveyProvider } from '@/components/DownloadSurvey/DownloadSurveyProvider'
import { EmptyState } from '@/components/EmptyState'

import { DATA_INDUK_YAYASAN_ROUTE } from '@/configs/constants'

import { SekolahNaungan } from './Modal/SekolahNaungan'
import { AreaSummaryStatsYayasan } from '../components/AreaSummaryStats/AreaSummaryStatsYayasan'
import { ButtonDownloadYayasan } from '../components/ButtonDownload/ButtonDownloadYayasan'
import { YAYASAN_MAP_COLUMN_TO_SORT_PARAM } from '../components/CustomTable/YayasanTable/columns'
import { YayasanTable } from '../components/CustomTable/YayasanTable/YayasanTable'
import { DataIndukSearchBox } from '../components/DataIndukSearchBox'
import { ApplyFilterButton } from '../components/Filter/ApplyFilterButton'
import { FilterJenis } from '../components/Filter/FilterJenis'
import { LastUpdatedAt } from '../components/LastUpdatedAt'
import { LocalSearch } from '../components/LocalSearch'
import { TitlePage } from '../components/TitlePage'
import { DATA_INDUK_TITLE_PREFIX, VARIANT } from '../constant'
import {
  ancestorToBreadcrumb,
  DEFAULT_LIMIT,
  getLatestWilayahName,
  getLogoProvinceLink,
} from '../helpers'
import { useFilterState } from '../hooks/useFilterState'
import { KODE_WILAYAH_LUAR_NEGERI } from '../provinces'
import { generateQueryKecamatan } from '../queryHelpers'

export const YayasanKecamatan = () => {
  const isMounted = useIsMounted()
  const params = useParams()
  const searchParams = useSearchParams()
  const router = useRouter()
  const provinceId = (params?.provinsi_id as string) || ''
  const kabKotId = (params?.kabkot_id as string) || ''
  const kecamatanId = (params?.kecamatan_id as string) || ''

  const queryPage = (searchParams.get('page') as string) || '1'
  const queryPageInt = parseInt(queryPage, 10) || 1

  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)
  const [isShowDialog, setIsShowDialog] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<{
    yayasanId: string
    name: string
  }>({
    yayasanId: '',
    name: '',
  })

  const [page, setPage] = useState<number>(queryPageInt)
  const [offset, setOffset] = useState<number>(
    (queryPageInt - 1) * DEFAULT_LIMIT
  )

  const isLuarNegeri = provinceId === KODE_WILAYAH_LUAR_NEGERI

  const {
    queryJenis,

    keyword,
    sortBy,
    sortDir,
    sortingState,
    selectedJenis,

    handleChangeSortBy,
    handleChangeSortDir,
    handleChangeJenis,

    handleSetKeyword,
    handleChangeSortingState,

    getFilterSearchParams,
  } = useFilterState({
    variant: VARIANT.YAYASAN,
    view: 'table',
  })

  const {
    data: dataWilayahAncestors,
    isPending: isPendingWilayahAncestors,
    error: errorWilayahAncestors,
  } = useWilayahBreadcrumbs(kecamatanId, {
    enabled: !!kecamatanId,
  })
  const { data: dataTableResponse, isPending: isPendingListOfSatpen } =
    useFetchSearchYayasan(
      offset,
      {
        kodeKecamatan: kecamatanId,
        keyword,
        sortBy: sortBy,
        sortDir: sortDir,
        jenis: queryJenis,
      },
      {
        enabled: !!kecamatanId,
      }
    )

  const handleOpenDialog = (row: BasicDataRow) => {
    setIsShowDialog(true)
    setSelectedRow({
      yayasanId: row?.['yayasanId'],
      name: row?.['namaYayasan'],
    })
  }

  const handleCloseDialog = () => {
    setIsShowDialog(false)
    setSelectedRow({
      yayasanId: '',
      name: '',
    })
  }

  const handleSubmitLocalSearch = (keywordLocalSearch: string) => {
    if (keywordLocalSearch !== '') {
      trackTypeLocalSearch({
        keyword: keywordLocalSearch,
        locationLevel: 'Kecamatan',
        locationId: kecamatanId,
        section: TrackerSection.YAYASAN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
    }
    handleSetKeyword(keywordLocalSearch)

    // Should reset the pagination
    setPage(1)
    setOffset(0)

    const searchParams = getFilterSearchParams({
      customKeyword: keywordLocalSearch,
    })
    router.replace(searchParams, { scroll: false })
  }

  const handleFilterSubmission = () => {
    setPage(1)
    setOffset(0)

    const searchParams = getFilterSearchParams({})
    const queryString = generateQueryKecamatan({
      searchParams,
      page: 1,
      withHash: false,
    })
    router.replace(queryString, { scroll: false })
  }

  const handleChangePage = (newPage: number) => {
    trackViewDataIndukKecamatan({
      provinceId,
      kabKotId,
      kecamatanId,
      section: TrackerSection.YAYASAN,
      subSection: TrackerSubSection.PAUD_DIKDASMEN,
    })
    if (newPage <= 1) {
      setPage(1)
      setOffset(0)

      const searchParams = getFilterSearchParams({})
      const queryString = generateQueryKecamatan({
        searchParams,
        page: 1,
        withHash: true,
      })
      router.replace(queryString)
    } else {
      setPage(newPage)
      setOffset((newPage - 1) * DEFAULT_LIMIT)

      const searchParams = getFilterSearchParams({})
      const queryString = generateQueryKecamatan({
        searchParams,
        page: newPage,
        withHash: true,
      })
      router.replace(queryString)
    }
  }

  useEffect(() => {
    if (provinceId && kabKotId && kecamatanId) {
      trackViewDataIndukKecamatan({
        provinceId,
        kabKotId,
        kecamatanId,
        section: TrackerSection.YAYASAN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
    }
  }, [provinceId, kabKotId, kecamatanId])

  useEffect(() => {
    if (isMounted()) {
      if (sortingState.length === 0) {
        handleChangeSortBy('')
        handleChangeSortDir('')

        const searchParams = getFilterSearchParams({
          customSortBy: '',
          customSortDir: '',
        })

        if (!isInitialLoad) {
          setPage(1)
          setOffset(0)

          const queryString = generateQueryKecamatan({
            searchParams,
            page: 1,
            withHash: false,
          })
          router.replace(queryString, { scroll: false })
        } else {
          const queryString = generateQueryKecamatan({
            searchParams,
            page: queryPageInt,
            withHash: false,
          })
          router.replace(queryString, { scroll: false })
        }

        setIsInitialLoad(false)
      } else if (sortingState.length === 1) {
        const sortParam = sortingState[0]

        const newSortDir = sortParam?.desc ? 'desc' : 'asc'
        const newSortBy =
          YAYASAN_MAP_COLUMN_TO_SORT_PARAM[sortParam?.id] || sortParam?.id

        handleChangeSortBy(newSortBy)
        handleChangeSortDir(newSortDir)

        const searchParams = getFilterSearchParams({
          customSortBy: newSortBy,
          customSortDir: newSortDir,
        })

        if (!isInitialLoad) {
          setPage(1)
          setOffset(0)

          const queryString = generateQueryKecamatan({
            searchParams,
            page: 1,
            withHash: false,
          })
          router.replace(queryString, { scroll: false })
        } else {
          const queryString = generateQueryKecamatan({
            searchParams,
            page: queryPageInt,
            withHash: false,
          })
          router.replace(queryString, { scroll: false })
        }

        setIsInitialLoad(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, sortingState])

  return (
    <>
      <section className="flex flex-col" id="search-section">
        <DataIndukSearchBox
          source={`${DATA_INDUK_YAYASAN_ROUTE}/${provinceId}/${kabKotId}/${kecamatanId}`}
          locationId={`${kecamatanId}`}
          locationLevel="Kecamatan"
          placeholder="Cari Data Yayasan"
          variant="yayasan"
        />
      </section>

      <section id="" className="bg-gray-5 pb-8">
        <Container>
          <div className="py-4">
            <Breadcrumbs
              items={
                isPendingWilayahAncestors
                  ? []
                  : ancestorToBreadcrumb({
                      level: 3,
                      withRoot: false,
                      pathPrefix: DATA_INDUK_YAYASAN_ROUTE,
                      nestedDistrict: dataWilayahAncestors?.district || null,
                    })
              }
            />
          </div>

          {!isPendingWilayahAncestors && errorWilayahAncestors ? (
            <div className="mb-4 min-h-[400px] w-full rounded-lg bg-white p-6">
              <EmptyState
                title="Data tidak ditemukan"
                description="Maaf data tidak dapat ditemukan, silahkan pilih data lain"
                size="sm"
              />
            </div>
          ) : (
            <>
              <div className="min-h-[500px] w-full rounded-lg bg-white p-6">
                <AreaSummaryStatsYayasan
                  withImage={true}
                  isPending={isPendingListOfSatpen}
                  total={
                    !isPendingListOfSatpen && dataTableResponse
                      ? dataTableResponse?.meta?.total || 0
                      : 0
                  }
                  title={`${DATA_INDUK_TITLE_PREFIX[VARIANT.YAYASAN]} di ${getLatestWilayahName({ data: dataWilayahAncestors })}`}
                  image={`${getLogoProvinceLink({
                    data: dataWilayahAncestors,
                    withDefault: true,
                  })}`}
                  level={isLuarNegeri ? 'country' : 'district'}
                  wrapperClass="mb-4"
                />
                <div className="mb-4 flex flex-col items-center justify-between gap-2 md:flex-row">
                  <TitlePage variant={VARIANT.YAYASAN} />
                  <DownloadSurveyProvider initialSource="DATA_INDUK">
                    <ButtonDownloadYayasan
                      disabled={isPendingListOfSatpen}
                      jenis={queryJenis}
                      keyword={keyword}
                      kodeKecamatan={kecamatanId}
                      sortBy={sortBy}
                      sortDir={(sortDir || 'asc') as 'asc' | 'desc'}
                      lastUpdatedAt={
                        dataTableResponse?.meta.lastUpdatedAt as string
                      }
                    />
                  </DownloadSurveyProvider>
                </div>

                <div className="mb-4 flex w-full flex-col lg:flex-row lg:items-center lg:justify-between">
                  <section className="py-4" id="filter-section">
                    <div className="grid grid-cols-1 items-end gap-2 md:grid-cols-2 lg:grid-cols-4">
                      <FilterJenis
                        jenis={[
                          { id: 'cabang', displayName: 'Cabang' },
                          { id: 'pusat', displayName: 'Pusat' },
                        ]}
                        value={selectedJenis}
                        onChange={handleChangeJenis}
                        loadingStatus="success"
                      />
                      <ApplyFilterButton onClick={handleFilterSubmission} />
                    </div>
                  </section>
                  <LocalSearch
                    onSubmit={handleSubmitLocalSearch}
                    initialValue={keyword}
                    placeholder="Cari nama yayasan"
                    formClass=""
                  />
                </div>

                <YayasanTable
                  page={page}
                  isPending={isPendingListOfSatpen}
                  data={dataTableResponse}
                  handleOpenDialog={handleOpenDialog}
                  handleChangePage={handleChangePage}
                  emptyMessage="Tidak dapat menemukan data yayasan, silahkan ubah pilihan terlebih dahulu"
                  sorting={sortingState}
                  onSortingChange={handleChangeSortingState}
                  onReset={() => {
                    setPage(1)
                    setOffset(0)
                    handleChangeSortBy('')
                    handleChangeSortDir('')
                    handleSetKeyword('')
                    handleChangeSortingState([])
                    router.replace('?refresh=1', { scroll: true })
                  }}
                />
              </div>

              {dataTableResponse?.meta?.lastUpdatedAt && (
                <LastUpdatedAt value={dataTableResponse?.meta.lastUpdatedAt} />
              )}
            </>
          )}
        </Container>
      </section>

      <SekolahNaungan
        name={selectedRow.name}
        yayasanId={selectedRow.yayasanId}
        show={isShowDialog}
        onClose={handleCloseDialog}
      />
    </>
  )
}
